/**
 * companyPagesBusinessService.js
 * Extended service for the "For Business" company pages feature.
 * Covers page CRUD, loan products, and cross-page team access.
 */
import axiosInstance from '../config/axios.config';

const BASE = '/leadflow/company-pages';

const companyPagesBusinessService = {

  /* ── Page listing & detail ─────────────────────────────── */
  getMyPages() {
    return axiosInstance.get(`${BASE}/my-pages`)
      .then(r => r.data)
      .catch(e => ({ success: false, message: e?.response?.data?.message || 'Failed to load your pages' }));
  },

  getPages({ search = '', loan_type = '', sub_product = '', perPage = 12, page = 1 } = {}) {
    const params = { search, per_page: perPage, page };
    if (loan_type)   params.loan_type   = loan_type;
    if (sub_product) params.sub_product = sub_product;
    return axiosInstance.get(BASE, { params })
      .then(r => r.data)
      .catch(e => ({ success: false, message: e?.response?.data?.message || 'Failed to load pages' }));
  },

  getPage(slug) {
    return axiosInstance.get(`${BASE}/${slug}`)
      .then(r => r.data)
      .catch(e => ({ success: false, message: e?.response?.data?.message || 'Failed to load page' }));
  },

  createPage(payload) {
    return axiosInstance.post(BASE, payload)
      .then(r => r.data)
      .catch(e => ({ success: false, message: e?.response?.data?.message || 'Failed to create page' }));
  },

  updatePage(slug, payload) {
    return axiosInstance.put(`${BASE}/${slug}`, payload)
      .then(r => r.data)
      .catch(e => ({ success: false, message: e?.response?.data?.message || 'Failed to update page' }));
  },

  toggleFollow(slug) {
    return axiosInstance.post(`${BASE}/${slug}/follow`)
      .then(r => r.data)
      .catch(e => ({ success: false, message: e?.response?.data?.message || 'Failed to follow/unfollow' }));
  },

  /* ── Posts ─────────────────────────────────────────────── */
  getPosts(slug, { perPage = 10, page = 1 } = {}) {
    return axiosInstance.get(`${BASE}/${slug}/posts`, { params: { per_page: perPage, page } })
      .then(r => r.data)
      .catch(e => ({ success: false, message: e?.response?.data?.message || 'Failed to load posts' }));
  },

  createPost(slug, payload) {
    return axiosInstance.post(`${BASE}/${slug}/posts`, payload)
      .then(r => r.data)
      .catch(e => ({ success: false, message: e?.response?.data?.message || 'Failed to create post' }));
  },

  /* ── Loan Products ─────────────────────────────────────── */
  getProducts(slug) {
    return axiosInstance.get(`${BASE}/${slug}/products`)
      .then(r => r.data)
      .catch(e => ({ success: false, message: e?.response?.data?.message || 'Failed to load products' }));
  },

  createProduct(slug, payload) {
    return axiosInstance.post(`${BASE}/${slug}/products`, payload)
      .then(r => r.data)
      .catch(e => ({ success: false, message: e?.response?.data?.message || 'Failed to create product' }));
  },

  updateProduct(slug, productId, payload) {
    return axiosInstance.put(`${BASE}/${slug}/products/${productId}`, payload)
      .then(r => r.data)
      .catch(e => ({ success: false, message: e?.response?.data?.message || 'Failed to update product' }));
  },

  deleteProduct(slug, productId) {
    return axiosInstance.delete(`${BASE}/${slug}/products/${productId}`)
      .then(r => r.data)
      .catch(e => ({ success: false, message: e?.response?.data?.message || 'Failed to delete product' }));
  },

  /* ── People ─────────────────────────────────────────────── */
  getPeople(slug, { search = '', perPage = 20, page = 1 } = {}) {
    return axiosInstance.get(`${BASE}/${slug}/people`, { params: { search, per_page: perPage, page } })
      .then(r => r.data)
      .catch(e => ({ success: false, message: e?.response?.data?.message || 'Failed to load people' }));
  },

  /* ── Managers / Access ─────────────────────────────────── */
  getManagers(slug) {
    return axiosInstance.get(`${BASE}/${slug}/managers`)
      .then(r => r.data)
      .catch(e => ({ success: false, message: e?.response?.data?.message || 'Failed to load managers' }));
  },

  addManager(slug, userId, role) {
    return axiosInstance.post(`${BASE}/${slug}/managers`, { user_id: userId, role })
      .then(r => r.data)
      .catch(e => ({ success: false, message: e?.response?.data?.message || 'Failed to add manager' }));
  },

  updateManagerRole(slug, managerId, role) {
    return axiosInstance.put(`${BASE}/${slug}/managers/${managerId}`, { role })
      .then(r => r.data)
      .catch(e => ({ success: false, message: e?.response?.data?.message || 'Failed to update role' }));
  },

  removeManager(slug, managerId) {
    return axiosInstance.delete(`${BASE}/${slug}/managers/${managerId}`)
      .then(r => r.data)
      .catch(e => ({ success: false, message: e?.response?.data?.message || 'Failed to remove manager' }));
  },

  /* ── Cross-page team access (bulk) ─────────────────────── */
  getTeamAccess() {
    return axiosInstance.get(`${BASE}/team-access`)
      .then(r => r.data)
      .catch(e => ({ success: false, message: e?.response?.data?.message || 'Failed to load team access' }));
  },

  saveTeamAccess(changes) {
    // changes: [{ profile_id, page_slug, role }]  role=null means remove
    return axiosInstance.post(`${BASE}/team-access/bulk`, { changes })
      .then(r => r.data)
      .catch(e => ({ success: false, message: e?.response?.data?.message || 'Failed to save team access' }));
  },

  /* ── Page lifecycle ─────────────────────────────────────── */
  deletePage(slug) {
    return axiosInstance.delete(`${BASE}/${slug}`)
      .then(r => r.data)
      .catch(e => ({ success: false, message: e?.response?.data?.message || 'Failed to delete page' }));
  },

  deactivatePage(slug) {
    return axiosInstance.post(`${BASE}/${slug}/deactivate`)
      .then(r => r.data)
      .catch(e => ({ success: false, message: e?.response?.data?.message || 'Failed to deactivate page' }));
  },

  /* ── Search platform users for adding as managers ──────── */
  searchUsers(query) {
    return axiosInstance.get('/leadflow/profiles/search', { params: { q: query, limit: 10 } })
      .then(r => r.data)
      .catch(e => ({ success: false, data: [] }));
  },

  /* ── Get tenant users (accessible to company_admin/owner/tenant_admin) ── */
  getTenantUsers({ limit = 200, search = '' } = {}) {
    const params = { limit };
    if (search) params.search = search;
    return axiosInstance.get('/users', { params })
      .then(r => r.data)
      .catch(e => ({ success: false, items: [] }));
  },

  /* ── Fetch all leads owned by the logged-in user ────────── */
  getMyLeads({ perPage = 200, page = 1 } = {}) {
    return axiosInstance.get('/leads', { params: { per_page: perPage, page } })
      .then(r => r.data)
      .catch(e => ({ success: false, data: [] }));
  },

  /* ── Upload media to S3 (same endpoint as social feed) ─── */
  uploadMedia(imageFiles) {
    const formData = new FormData();
    imageFiles.forEach(file => formData.append('files[]', file));
    return axiosInstance.post('/leadflow/feed/media', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    }).then(r => r.data)
      .catch(e => ({ success: false, message: e?.response?.data?.message || 'Failed to upload media' }));
  },

  /* ── Create post — uploads images to S3 first ──────────── */
  async createPostWithMedia(slug, { content, videoUrl }, imageFiles = []) {
    let mediaUrls = [];
    let mediaType = null;

    // 1. Upload images to S3 first
    if (imageFiles.length > 0) {
      const uploadRes = await this.uploadMedia(imageFiles);
      if (!uploadRes?.success) {
        return { success: false, message: uploadRes?.message || 'Image upload failed' };
      }
      mediaUrls = (uploadRes.data || []).map(m => (typeof m === 'string' ? m : m.url));
      mediaType = 'image';
    }

    // 2. Add video URL if present
    if (videoUrl?.trim()) {
      mediaUrls.push(videoUrl.trim());
      mediaType = mediaUrls.length > 1 ? 'mixed' : 'video';
    }

    // 3. Create post with S3 URLs
    const payload = {
      content: content || '',
      ...(mediaUrls.length > 0 && { media_urls: mediaUrls, media_type: mediaType }),
    };

    return axiosInstance.post(`${BASE}/${slug}/posts`, payload)
      .then(r => r.data)
      .catch(e => ({ success: false, message: e?.response?.data?.message || 'Failed to create post' }));
  },

  /* ── Rate a company page (1–5 stars) ────────────────────── */
  rate(slug, { rating, review = null } = {}) {
    return axiosInstance.post(`${BASE}/${slug}/rate`, { rating, review })
      .then(r => r.data)
      .catch(e => ({ success: false, message: e?.response?.data?.message || 'Failed to submit rating' }));
  },

  /* ── Delete a company page post ─────────────────────────── */
  deletePost(slug, postId) {
    return axiosInstance.delete(`${BASE}/${slug}/posts/${postId}`)
      .then(r => r.data)
      .catch(e => ({ success: false, message: e?.response?.data?.message || 'Failed to delete post' }));
  },
};

export default companyPagesBusinessService;
